import type { Article } from "../types";

export const learningArticles: Article[] = [
  // ---------------------------------------------------------------- Home
  {
    id: "learning-home-tour",
    title: "Finding your way around Home",
    category: "learning",
    level: "start",
    summary: "What the Home page shows, what each area is for, and why some cards may be missing for you.",
    tags: ["home", "start", "cards", "modules", "navigation", "current account", "roles", "hidden", "missing", "sidebar"],
    body: [
      { type: "p", text: "Home is the first page you see. It is a map of everything your agency can do in Flowio, with one card for each tool (called a **module**). Click a card to open the tool." },
      { type: "h", text: "What is on the page" },
      {
        type: "list",
        items: [
          "**Greeting.** Good morning, afternoon or evening, followed by your first name and your agency's name.",
          "**Current account card.** Shows the customer or prospect you are working in right now, with its business and location. Click it to open that account's overview.",
          "**Module cards.** Grouped under headings: Home, Sales, Delivery, Insights, Learning and Admin. Each shows an icon, a name and a one-line description.",
        ],
      },
      { type: "h", text: "The current account" },
      { type: "p", text: "An **account** is one customer or prospect. Many tools, including call scripts, the missed-call calculator and the wiki, fill in that account's details for you, so it helps to choose one first." },
      {
        type: "steps",
        items: [
          { title: "Look at the card under the greeting", body: "If it says No account selected, nothing is chosen yet." },
          { title: "Use the switcher in the top bar", body: "Click No account selected, or the small arrow next to the account name, then search by name, city or owner and pick one." },
          { title: "Confirm", body: "The Current account card now shows that account. Click it to open its overview.", to: "/pipeline", toLabel: "Open Pipeline to find accounts" },
        ],
      },
      { type: "p", text: "Some cards, such as Live Call, Call Logger, Fulfillment and Client Portal, belong to a single account. When an account is selected they open inside it. Otherwise they open at the general page." },
      { type: "h", text: "The areas" },
      {
        type: "table",
        head: ["Area", "What it is for", "Cards"],
        rows: [
          ["Home", "Start here", "Today, Pipeline"],
          ["Sales", "In the pipeline", "Live Call, Revenue Calculator, VSL Hosting, Contract Builder"],
          ["Delivery", "For the customer", "Fulfillment, Checklist Templates, Client Portal"],
          ["Insights", "Know your numbers", "Command Center, Sales Tracking, Setter Tracking, Booking Board, Status Report, Audit Reports"],
          ["Learning", "Sharpen the motion", "Wiki, Playbook, Drill Range, Objections, Mastery"],
          ["Admin", "Workspace controls", "Settings, Data Admin"],
        ],
      },
      { type: "h", text: "Why a card might be missing" },
      { type: "p", text: "Two things hide cards. First, your **role**: some tools need a permission your role does not have. Second, your agency can switch modules off, in which case they disappear from Home and the sidebar." },
      {
        type: "table",
        head: ["Card", "Needs this permission", "Roles that have it by default"],
        rows: [
          ["Call Logger", "Call log: create", "Owner, Admin, Closer"],
          ["Contract Builder", "Contract Builder: read", "Owner, Admin, Closer"],
          ["Fulfillment, Checklist Templates", "Customer checklists: read", "Owner, Admin, Fulfillment"],
          ["Client Portal", "Portal forms: read", "Owner, Admin, Fulfillment"],
          ["Command Center", "MRR and subscriptions: read", "Owner, Admin"],
          ["Status Report", "Scoreboard and reports: read", "Owner, Admin"],
          ["Audit Reports", "Audit reports: read", "Owner, Admin, Closer, Setter"],
          ["Settings", "Integrations and API keys: update", "Owner, Admin"],
          ["Data Admin", "Data Admin grid: read", "Owner, Admin"],
        ],
      },
      { type: "callout", tone: "note", text: "The Learning cards and most Sales and Insights cards have no permission requirement, so everyone sees them unless the agency has turned them off. Agencies can change these defaults in Settings, Permissions." },
      { type: "callout", tone: "tip", text: "If you can see a card in a colleague's Home but not in yours, ask an admin to check your role. If you type a page's address directly and your role lacks access, Flowio sends you back to Home." },
      { type: "h", text: "The small marks on a card" },
      { type: "list", items: ["A small orange dot means a **hero feature**, one of the most important tools.", "A **Beta** badge means the tool is still being tested.", "Hover either mark to see a short explanation."] },
    ],
    related: ["learning-wiki-write-edit", "learning-mastery-ranks", "glossary"],
  },

  // ---------------------------------------------------------------- Wiki
  {
    id: "learning-wiki-write-edit",
    title: "How to write and edit a wiki page",
    category: "learning",
    level: "basic",
    summary: "Create a space and a page, write with the editor, save, preview and publish it for your team.",
    tags: ["wiki", "page", "space", "sop", "editor", "draft", "publish", "unpublish", "delete", "toolbar", "knowledge base"],
    body: [
      { type: "p", text: "The **Wiki** is your agency's shared knowledge base: procedures (SOPs), playbooks and reference notes. Pages live in **spaces**, which work like folders. A page that is not in a space appears under **Unfiled**." },
      { type: "callout", tone: "note", text: "Everyone can read published pages. Creating, editing, publishing and deleting need wiki permissions. By default only owners and admins have them. If you do not see New page, ask an admin." },
      { type: "h", text: "Create a space (optional)" },
      {
        type: "steps",
        items: [
          { title: "Open the Wiki", to: "/wiki", toLabel: "Open Wiki" },
          { title: "Click New space", body: "Type a Name, such as Sales Playbook. The Icon (emoji) box is optional and shows before the name." },
          { title: "Click Create", body: "The space appears in the list, ready for pages." },
        ],
      },
      { type: "h", text: "Write a page" },
      {
        type: "steps",
        items: [
          { title: "Click New page", body: "Type a Title and choose a Space, or leave it as Unfiled." },
          { title: "Click Create", body: "The page opens straight in the editor and starts as a **draft**, which means only wiki editors can see it." },
          { title: "Write", body: "Use the toolbar above the writing area: three heading sizes, bold, italic, inline code, bulleted and numbered lists, quote, link, undo and redo. Hover any button to see its name." },
          { title: "Add tokens if you want", body: "Type placeholders such as {{shopName}}. They fill in on the preview. See the tokens guide for the full list." },
          { title: "Click Save", body: "The button is greyed out until you change something, and reads Saved once your work is stored." },
        ],
      },
      { type: "callout", tone: "warn", text: "Click Save before Preview. Leaving the editor with unsaved changes asks 'Discard unsaved changes?', and choosing Discard throws your edits away." },
      { type: "h", text: "Preview and publish" },
      {
        type: "steps",
        items: [
          { title: "Click Preview", body: "This leaves the editor and shows the page as readers see it, with tokens filled in for the active account." },
          { title: "Click Publish", body: "The page becomes visible to the whole team. The draft label disappears. Click Unpublish later to turn it back into a draft." },
        ],
      },
      { type: "h", text: "Edit an existing page" },
      {
        type: "steps",
        items: [
          { title: "Open the page from the list", to: "/wiki", toLabel: "Open Wiki" },
          { title: "Click Edit", body: "You can change the Title and the content." },
          { title: "Click Save, then Preview", body: "Changes go live immediately on a published page. The page shows who updated it and when." },
        ],
      },
      { type: "h", text: "Find drafts and delete pages" },
      { type: "list", items: ["Tick **Show drafts** at the top of the Wiki to list unpublished pages. Each has a draft badge.", "The trash button next to Publish deletes a page after you confirm. Deleting a page cannot be undone."] },
      {
        type: "faq",
        items: [
          { q: "I published a page but a teammate cannot see it.", a: "Check the page no longer shows a draft badge. If it is published, check they are in the same agency and signed in." },
          { q: "Can I move a page to another space?", a: "The page screen does not offer this. Choose the space when you create the page." },
          { q: "I see the raw {{tokens}} in the editor.", a: "That is normal. The editor shows what you typed. Click Preview to see them filled in." },
        ],
      },
    ],
    related: ["learning-wiki-tokens", "learning-home-tour", "glossary"],
  },

  {
    id: "learning-wiki-tokens",
    title: "Wiki and script tokens explained",
    category: "learning",
    level: "basic",
    summary: "Tokens are placeholders like {{shopName}} that fill in with the active account's details in the wiki, Playbook and Objections.",
    tags: ["tokens", "placeholders", "variables", "{{", "shopName", "ownerName", "ticket", "avgTicket", "revMo", "merge fields", "unresolved", "red", "blue", "wiki", "scripts"],
    body: [
      { type: "p", text: "A **token** is a word inside double curly braces, such as `{{shopName}}`. When a page is shown, Flowio swaps each token for the matching value. That way one script or wiki page works for every prospect." },
      { type: "h", text: "How tokens get their values" },
      {
        type: "list",
        items: [
          "When you choose an account in the header, Flowio copies that account's details (name, owner, city, website, revenue, average ticket, reviews) into a set of values stored **in your browser**.",
          "Your agency's name and your own name fill in automatically, whether or not an account is chosen.",
          "Numbers you learn on a call, such as missed calls per day, are entered in Live Call or the Revenue Calculator and are not changed when you move between pages.",
          "Starting a **New Call** clears the per-call values so the next account fills in fresh.",
        ],
      },
      { type: "callout", tone: "note", text: "Values fill in once per account. If you type a different number during a call, moving to another page will not overwrite it." },
      { type: "h", text: "The tokens you can use" },
      {
        type: "table",
        head: ["Token", "What it shows", "Where the value comes from"],
        rows: [
          ["{{shopName}}", "The business name", "The account's name"],
          ["{{ownerName}} or {{name}}", "The owner's name", "The account's owner name"],
          ["{{city}}", "The city", "The account's city, or the location found by research if the city is blank"],
          ["{{website}}", "The website address", "The account's website"],
          ["{{revMo}}", "Monthly revenue, with a dollar sign", "The account's monthly revenue"],
          ["{{avgTicket}} or {{ticket}}", "Average job value, with a dollar sign", "The account's average ticket. Starts at $4,200 until an account fills it in"],
          ["{{reviews}}", "Number of reviews", "The account's review count"],
          ["{{partnerName}}", "The decision-maker's partner", "No screen currently fills this in, so it normally stays as typed"],
          ["{{company}}", "Your own agency's name", "Your agency's display name (or its name if none is set)"],
          ["{{repName}}", "Your name", "The signed-in person"],
          ["{{missedPerDay}}", "Missed calls per day", "Starts at 3. Change it in Live Call or the Revenue Calculator"],
          ["{{daysWeek}}", "Days open per week", "Starts at 5.5, or the days open found by research"],
          ["{{apptRatio}}", "Share of missed calls that book", "Starts at 90%. Set on the calculators"],
          ["{{closeRate}}", "Share of appointments that close", "Starts at 75%. Set on the calculators"],
          ["{{capturePct}}", "Booking share times close share", "Worked out for you (68% with the defaults)"],
        ],
      },
      { type: "p", text: "Token names are case sensitive: `{{shopName}}` works but `{{shopname}}` does not. Spaces just inside the braces are fine." },
      { type: "h", text: "What the colours mean" },
      {
        type: "list",
        items: [
          "On the **Playbook** and **Objections** pages, filled-in tokens show in **blue** and tokens that could not be filled show in **red**.",
          "Red matters because an unfilled token would be read out literally on a Voice AI call.",
          "In the **Wiki**, tokens are only replaced on the Preview. The editor and the saved page keep the raw tokens.",
        ],
      },
      { type: "h", text: "Fixing a token that will not fill in" },
      {
        type: "steps",
        items: [
          { title: "Check an account is selected", body: "With no account, account tokens stay as typed. Pick one in the top bar." },
          { title: "Check the account has that detail", body: "If the account has no average ticket or city, that token stays blank. Add the detail on the account." , to: "/pipeline", toLabel: "Open Pipeline" },
          { title: "Check the spelling", body: "A misspelt or unknown token is left exactly as you typed it." },
          { title: "Refresh the details", body: "If you changed an account and the old value lingers, start a New Call or reselect the account." },
        ],
      },
    ],
    related: ["learning-wiki-write-edit", "learning-playbook-find-things", "learning-objection-cards", "glossary"],
  },

  // ------------------------------------------------------------ Playbook
  {
    id: "learning-playbook-find-things",
    title: "How to find things in the Playbook",
    category: "learning",
    level: "basic",
    summary: "The Playbook lays out a whole sales call in order. Learn its parts, how to search it and how to copy a line.",
    tags: ["playbook", "script", "call structure", "intro", "discovery", "pitch", "close", "must cover", "search", "copy", "before the call", "four things"],
    body: [
      { type: "p", text: "The **Playbook** is the structure of a sales call. It is not a word-for-word script. It gives you the sequence, plus exact wording for the moments that decide whether a call is won." },
      { type: "steps", items: [{ title: "Open the Playbook", to: "/playbook", toLabel: "Open Playbook" }, { title: "Pick an account first", body: "Choose the account in the top bar. The grey note near the top disappears and lines show the real business details instead of generic tokens." }] },
      { type: "h", text: "Page layout, top to bottom" },
      {
        type: "table",
        head: ["Part", "What it holds"],
        rows: [
          ["The four things", "Four numbered questions that decide a call. You will tick these when you score a drill."],
          ["Before the call", "Four checks to do every time: their business, their market, your numbers, and calling them. The highlighted one is marked highest value."],
          ["Search box", "Search the playbook. Filters the phases below."],
          ["Intro (0 to 3 min)", "Rapport, authority, and establishing that you lead the call."],
          ["Discovery (3 to 33 min)", "The main part of the call, where under 35% of the talking should be yours. Questions about their problem, numbers, phone handling, what they have tried, goals and who else decides."],
          ["Pitch (33 to 43 min)", "Using their numbers, not big generic ones, to present the solution."],
          ["Close (43 to 60 min)", "Staying on the prospect's side of the table. Isolate the concern, then ask for the sale three times."],
        ],
      },
      { type: "p", text: "The minute ranges assume a 60-minute call. They are guides to pacing, not a strict clock." },
      { type: "h", text: "Reading a line" },
      {
        type: "list",
        items: [
          "The small heading on each line says what kind it is: **say** (words to speak), **ask** (a question), **do** (an action for you) or **note** (background).",
          "A **must cover** badge marks a moment you should never skip.",
          "Italic text underneath is a **coaching note** explaining why the line matters.",
          "Blue words are filled in from the account. Red words are tokens that could not be filled in.",
        ],
      },
      { type: "h", text: "Search and copy" },
      {
        type: "steps",
        items: [
          { title: "Type in Search the playbook", body: "Only lines whose wording or coaching note contains your text stay visible. Phases with no match are hidden." },
          { title: "Clear the box", body: "Delete the text to bring every phase back." },
          { title: "Click the copy icon on a line", body: "It copies the line with tokens already filled in, so you can paste it into a message." },
        ],
      },
      { type: "callout", tone: "tip", text: "Preparing for a call? Read Before the call, then skim every must cover line. During a live call, use Live Call, which puts the same structure beside a timer." },
      {
        type: "faq",
        items: [
          { q: "Why do I see red words in a line?", a: "Those tokens have no value yet. Select an account in the top bar." },
          { q: "Can I change the Before the call list?", a: "Yes. An admin can add Precall checklist blocks in Settings, Content blocks. Once any exist they replace the built-in four." },
        ],
      },
    ],
    related: ["learning-wiki-tokens", "learning-objection-cards", "learning-run-a-drill", "glossary"],
  },

  // ---------------------------------------------------------- Drill Range
  {
    id: "learning-run-a-drill",
    title: "How to run a drill and read the score",
    category: "learning",
    level: "basic",
    summary: "Practise a call against a pretend prospect by voice or text, score yourself honestly and log the result.",
    tags: ["drill", "drill range", "roleplay", "practice", "voice", "text", "elevenlabs", "chatgpt", "claude", "score", "log drill", "self score", "api key", "microphone", "prompt"],
    body: [
      { type: "p", text: "The **Drill Range** lets you rehearse a sales call against a pretend prospect, called a **persona**, before you face a real one. You then grade yourself and log the drill. Logged drills feed your Mastery ranks." },
      { type: "callout", tone: "note", text: "Flowio does not grade drills for you and needs no API keys for them. Voice practice opens an outside ElevenLabs page, and text practice uses your own ChatGPT or Claude chat. The score is the number you enter." },
      { type: "h", text: "Before you start" },
      {
        type: "list",
        items: [
          "The text prompt says you are a rep for your agency. If an admin has filled in What you sell (Settings, Content), that is included too.",
          "For voice, you need a microphone and a browser that lets the ElevenLabs page use it.",
          "For text, you need a chat window with an AI assistant, such as ChatGPT or Claude.",
        ],
      },
      { type: "h", text: "Run a drill" },
      {
        type: "steps",
        items: [
          { title: "Open the Drill Range", to: "/drill-range", toLabel: "Open Drill Range" },
          { title: "Click a persona card", body: "A window opens with the persona's description, the launch options and the scoring panel. Turn on Suggested path first if you want a recommended order." },
          { title: "Start the roleplay", body: "Voice: click a button such as Full Call, Discovery Drill or Objection Gauntlet. It opens an ElevenLabs page in a new tab. Text: click Copy text-roleplay prompt." },
          { title: "Play it out", body: "For text, paste the prompt into your AI chat, say start, and answer as the rep. When you finish, say SCORE and the AI will grade you on the sales structure, diagnosing before prescribing, getting the prospect to say their own numbers, asking about partners, and asking for the sale three times." },
          { title: "Score the rep", body: "Back in Flowio, drag the Overall slider to a number from 0 to 100 (it starts at 75)." },
          { title: "Tick the four things", body: "Tick only the habits you really did: Diagnosed first, They said #s, Asked partner and Asked 3x. Hover a button to read the full question." },
          { title: "Log it", body: "Click Log drill. You see a confirmation with the score, and the totals at the top of the page update." },
        ],
      },
      { type: "callout", tone: "warn", text: "Voice buttons are only offered for the built-in first persona, Rick Delgado. The other personas are practised with the text prompt." },
      { type: "h", text: "Reading the numbers on the page" },
      {
        type: "table",
        head: ["Where", "What it means"],
        rows: [
          ["Drills run", "How many drills have been logged by your agency, all reps combined."],
          ["Avg score", "The average of all logged self-scores."],
          ["Mastered", "How many personas have a best score of 85 or more, out of 10."],
          ["Trophy badge on a persona card", "Your best logged score for that persona. It turns green at 85 or more."],
          ["Orange skill label", "The skill that persona tests. An orange label with a warning line marks a walk-away persona."],
        ],
      },
      { type: "h", text: "Walk-away personas" },
      { type: "p", text: "Two personas, Jerry Kovacs and Angela Ruiz, are the wrong fit for the product. Closing them is the failure. The right outcome is to decline politely and ask for a referral. After those drills, a button appears in the scoring panel: tick **Correctly walked away + asked for referral** only if you truly did that." },
      {
        type: "faq",
        items: [
          { q: "Does voice practice cost anything or need a key from me?", a: "Flowio does not ask for one. The voice agents are hosted on ElevenLabs, and whether that page needs an account or limits usage is up to ElevenLabs." },
          { q: "Why does a voice drill show up as a text drill?", a: "The Log drill button records every drill as a text drill. The score and ticks are still saved correctly." },
          { q: "Can I log the same persona again?", a: "Yes. Every drill is saved. The persona card shows your best score, and the totals include every drill." },
          { q: "Can I change a drill after logging it?", a: "The page has no edit or delete for logged drills, so score carefully." },
        ],
      },
    ],
    related: ["learning-personas-scoring", "learning-mastery-ranks", "learning-objection-cards", "glossary"],
  },

  {
    id: "learning-personas-scoring",
    title: "The ten personas and what each one tests",
    category: "learning",
    level: "basic",
    summary: "Who the pretend prospects are, the skill each one trains, and which two punish you for closing.",
    tags: ["persona", "personas", "rick", "marcus", "dana", "ty", "bill", "rosa", "kenny", "sunny", "jerry", "angela", "walk-away", "suggested path", "inverted", "skills"],
    body: [
      { type: "p", text: "Each **persona** is a pretend shop owner with a different personality and a different weakness for you to practise against. The built-in ten are below, in the order the Drill Range lists them. Your agency can replace them with its own." },
      {
        type: "table",
        head: ["#", "Name", "Business", "Skill it tests"],
        rows: [
          ["1", "Rick Delgado", "Collision shop, Toledo, $85k a month", "Baseline: vague answers, a bad agency history, and a partner you only find by asking"],
          ["2", "Marcus Webb", "Mechanical shop, Greenville, $38k a month", "Call control: friendly and chatty, will burn your hour"],
          ["3", "Dana Kowalczyk", "Collision shop, Buffalo, $140k a month", "Precision under scrutiny: an ex-insurance adjuster who punishes vagueness"],
          ["4", "Ty Nguyen", "Collision shop, Sacramento, $22k a month", "Build versus buy: says he could make it himself"],
          ["5", "Bill Hargrove", "Collision shop, Springfield MO, $95k a month", "Fear of the product: worried customers will not like an AI"],
          ["6", "Rosa Delacruz", "Collision and repair, Phoenix, $65k a month", "Frame control: distracted, wants an emailed price fast"],
          ["7", "Kenny Pruitt", "Collision shop, Knoxville, $180k a month", "Price discipline: demands discounts, never concede a dollar"],
          ["8", "Sunny Patel", "Collision shop, Edison NJ, $110k a month", "Decision-maker mapping: ask about partners early or lose"],
          ["9", "Jerry Kovacs", "Fleet shop, Cleveland, $75k a month", "Honest disqualification (walk-away persona)"],
          ["10", "Angela Ruiz", "Paint and body, San Antonio, $18k a month", "Qualification and ethics (walk-away persona)"],
        ],
      },
      { type: "h", text: "Suggested path" },
      { type: "p", text: "Turn on **Suggested path** at the top right of the Drill Range to number the built-in personas in a recommended order, starting with the easier ones:" },
      { type: "list", items: ["1. Marcus Webb", "2. Rosa Delacruz", "3. Rick Delgado", "4. Bill Hargrove", "5. Dana Kowalczyk", "6. Ty Nguyen", "7. Kenny Pruitt", "8. Sunny Patel", "9. Jerry Kovacs", "10. Angela Ruiz"] },
      { type: "callout", tone: "note", text: "The switch does nothing if your agency has added its own personas. Custom personas are always shown in the order they were created." },
      { type: "h", text: "How scoring works" },
      {
        type: "list",
        items: [
          "The **Overall** score is a slider you set from 0 to 100 after the roleplay. It is a self-score.",
          "The **four things** are yes or no ticks: did you diagnose before prescribing, did the prospect say their own numbers, did you ask about the decision-makers during Discovery, and did you ask for the sale at least three times.",
          "For the two walk-away personas there is a fifth tick, **Correctly walked away + asked for referral**.",
          "A persona counts as **Mastered** when your best score for it is 85 or higher.",
        ],
      },
      { type: "callout", tone: "tip", text: "Not sure what score to give? Ask the AI to SCORE you in the text roleplay, then use its feedback to set your slider." },
    ],
    related: ["learning-run-a-drill", "learning-mastery-ranks", "learning-customize-training-content", "glossary"],
  },

  // -------------------------------------------------------------- Mastery
  {
    id: "learning-mastery-ranks",
    title: "How the Mastery ranks work",
    category: "learning",
    level: "basic",
    summary: "The five ranks up to Certified, the exact numbers each one needs, and how to move them.",
    tags: ["mastery", "ranks", "rank", "certified", "certification", "green", "control", "diagnosis", "pressure", "judgment", "four things", "walk-away", "requirements", "gate", "progress"],
    body: [
      { type: "p", text: "**Mastery** turns your drill history into five ranks, ending in **Certified**. There is no manager sign-off. Every rank is unlocked by numbers from your logged drills, so every gate is something you can move by practising." },
      { type: "h", text: "The five ranks" },
      {
        type: "table",
        head: ["Rank", "Name", "What is needed"],
        rows: [
          ["1", "Green", "At least one drill logged."],
          ["2", "Control", "Two different personas with a best score of 80 or more."],
          ["3", "Diagnosis", "Four different personas with a best score of 85 or more."],
          ["4", "Pressure", "Six different personas with a best score of 85 or more, and the four things averaging 70% or more across all your drills."],
          ["5", "Judgment (Certified)", "Correct walk-aways logged on two different personas, and an average of 80 or more across your last three scored drills."],
        ],
      },
      { type: "h", text: "Terms used in the gates" },
      {
        type: "list",
        items: [
          "**Best score** is the highest self-score you have logged against a persona. Repeating one persona never counts twice.",
          "**The four things average** works out, for each of the four ticks, the share of drills where it was ticked. It then averages those four percentages.",
          "**Correct walk-away** is logged by ticking that button on a walk-away persona. It counts once per persona.",
          "**Last three scored drills** are the three most recent drills that have a score.",
        ],
      },
      { type: "h", text: "Reading the page" },
      {
        type: "list",
        items: [
          "The **ring** shows how many of the 5 ranks you pass right now. Under it you see Certified or In training, and the name of the next rank to aim for.",
          "The **four things meters** show each habit's lifetime percentage. The marker sits at a 90% benchmark. A dash means no data yet.",
          "Each **rank card** shows a padlock until you meet its gate, then a tick and a Passed badge.",
          "**Go drill** jumps to the Drill Range.",
        ],
      },
      { type: "callout", tone: "warn", text: "Each rank is checked on its own, against your drills as they are today, not as a running ladder. A rank can therefore show as passed and later not passed if the numbers change, for example when the last-three average slips." },
      { type: "callout", tone: "note", text: "The drills counted are your whole agency's, not only yours, so 'lifetime, across every rep' is exactly that. Rank progress therefore reflects the team. Only drills from the active data mode (demo or live) are shown." },
      { type: "h", text: "How to move up" },
      {
        type: "steps",
        items: [
          { title: "Log your first drill", body: "This unlocks Green.", to: "/drill-range", toLabel: "Open Drill Range" },
          { title: "Spread your practice", body: "Ranks 2 to 4 count different personas. Work down the Suggested path rather than repeating one." },
          { title: "Tick honestly", body: "The four things average feeds rank 4, so it only rises when you really do those habits." },
          { title: "Finish with walk-aways", body: "Drill Jerry Kovacs and Angela Ruiz, decline the sale, ask for a referral, and tick the walk-away button on each." },
          { title: "Keep scoring high", body: "Rank 5 needs your three most recent scored drills to average 80 or more." },
          { title: "Check your progress", to: "/mastery", toLabel: "Open Mastery" },
        ],
      },
      {
        type: "faq",
        items: [
          { q: "I have my own personas. Do ranks still work?", a: "Yes. The gates count distinct personas by score, not by name. The Mastered figure on the Drill Range still says out of 10, though." },
          { q: "Is there a limit on how many drills count?", a: "Mastery reads your agency's 200 most recent drills, which is plenty for these gates." },
        ],
      },
    ],
    related: ["learning-run-a-drill", "learning-personas-scoring", "glossary"],
  },

  // ----------------------------------------------------------- Objections
  {
    id: "learning-objection-cards",
    title: "How to use the objection cards on a call",
    category: "learning",
    level: "basic",
    summary: "Match what the prospect says to a card, make the first move, then use the labelled lines. Also how to find a card fast.",
    tags: ["objections", "objection", "cards", "rebuttal", "funds", "timing", "partner", "uncertainty", "price", "discount", "think about it", "first move", "never", "walk-away", "disqualify", "ctrl k", "search"],
    body: [
      { type: "p", text: "An **objection** is pushback from a prospect, such as 'that's more than I was thinking'. The Objections page gives you an instant card for each one. The idea: every objection is really about **funds** (money), **timing**, a **partner** (someone else decides) or **uncertainty** (will it work?). Find which one it is before you try to solve it, and never discount." },
      { type: "h", text: "Before the call" },
      { type: "steps", items: [{ title: "Pick the account", body: "Choose it in the top bar so lines show real names and numbers. If none is chosen, a grey note reminds you." }, { title: "Open the Objections page", to: "/objections", toLabel: "Open Objections" }] },
      { type: "h", text: "During the call" },
      {
        type: "steps",
        items: [
          { title: "Match the phrase", body: "Each card is headed by a quoted phrase, such as 'How do I know it'll work?'. Find the closest one." },
          { title: "Check the label", body: "The orange word above it (FUNDS, TIMING, PARTNER or UNCERTAINTY) is the real reason behind the pushback." },
          { title: "Make the first move", body: "The blue line under the phrase is your opening response, such as isolating the concern or asking a question." },
          { title: "Use the labelled lines", body: "Boxes such as The split or Uncover give the wording. Some branch by situation, for example 'If doubt' or 'If genuinely cash'. Click the copy icon to copy one." },
          { title: "Read the red note", body: "A red Never box is a hard rule, for example never discount, not by a dollar." },
        ],
      },
      { type: "h", text: "The five built-in cards" },
      {
        type: "table",
        head: ["What they say", "Type", "Core idea"],
        rows: [
          ["That's more than I was thinking", "Funds", "Isolate first, then split whether it is cash or doubt. Never discount."],
          ["How do I know it'll work?", "Uncertainty", "Reframe the burden of proof, then have them call the demo line as a customer."],
          ["I tried marketing before and it didn't work", "Uncertainty", "Not an objection but an invitation. Dig for details."],
          ["I need to talk to my partner", "Partner", "Recover by asking what the partner would push back on, and offer a three-way call. If you hear it at the close, the miss was in Discovery."],
          ["Let me think about it", "Timing", "It usually hides a real objection. Ask what specifically, solve that, then ask again."],
        ],
      },
      { type: "callout", tone: "note", text: "The uncertainty card includes a demo-line box. If it reads 'set your Voice AI demo line', no number has been set yet, so do not read it out." },
      { type: "h", text: "When it isn't a fit" },
      { type: "p", text: "At the bottom is a card titled **When it isn't a fit — say so**, with a walk-away script. Some prospects should not buy, such as very small shops or fleet operations with no consumer calls. Politely declining and asking for a referral is logged as a win, not a loss." },
      { type: "h", text: "Finding a card in a hurry" },
      { type: "list", items: ["Press **Ctrl+K** (or Cmd+K on a Mac), or type **/** when you are not typing in a box, to open search. Scroll to the Objections group, or type part of the phrase, and press Enter.", "The page then scrolls to that card and outlines it."] },
      { type: "callout", tone: "tip", text: "Rehearse these before real calls. Drill Range's Objection Gauntlet voice agent and the Kenny Pruitt and Sunny Patel personas exercise the price and partner cards." },
      {
        type: "faq",
        items: [
          { q: "What do the red and blue words in a line mean?", a: "They are tokens. Blue ones are filled in from the account. Red ones are not filled in yet, so select an account. Copy the line only when it is blue." },
          { q: "Can we write our own objection cards?", a: "Yes. An admin can add Objection responses in Settings, Content blocks. Once any exist they replace the built-in five." },
        ],
      },
    ],
    related: ["learning-wiki-tokens", "learning-playbook-find-things", "learning-run-a-drill", "learning-customize-training-content", "glossary"],
  },

  // ------------------------------------------------- Customising content
  {
    id: "learning-customize-training-content",
    title: "Use your own personas, objections and pre-call checks",
    category: "learning",
    level: "advanced",
    summary: "Admins can replace the built-in training content with the agency's own in Settings, Content blocks.",
    tags: ["content blocks", "custom personas", "custom objections", "precall", "settings", "seed starters", "replace", "built-in", "admin"],
    body: [
      { type: "p", text: "The Drill Range, Objections and Playbook pages come with built-in content. An admin can supply the agency's own instead, in **Settings, Content blocks**." },
      { type: "callout", tone: "note", text: "Settings is only visible to roles with the integrations permission, which by default means owners and admins." },
      {
        type: "steps",
        items: [
          { title: "Open Settings and go to Content blocks", to: "/settings?tab=blocks", toLabel: "Open Content blocks" },
          { title: "Pick the type from the dropdown", body: "Choose Personas (Drill Range), Objection responses (Objections) or Precall checklist (Playbook, Before the call)." },
          { title: "Add blocks", body: "Click New and fill in the fields, or click Seed starters to add a small default set you can then edit." },
          { title: "Check the page", body: "Open the matching page and confirm your content is showing." },
        ],
      },
      { type: "callout", tone: "warn", text: "All or nothing: as soon as you add even one block of a type, the built-in items of that type stop showing on that page. Seed starters first if you want to keep the originals." },
      { type: "list", items: ["A persona block has a name, type, city, revenue, the skill it tests and how it drills.", "Custom personas show in creation order, and the Suggested path switch no longer applies.", "The Mastered figure on the Drill Range still reads out of 10 even if you have a different number of personas."] },
    ],
    related: ["learning-personas-scoring", "learning-objection-cards", "learning-playbook-find-things", "glossary"],
  },

  // ---------------------------------------------------------- Data Admin
  {
    id: "admin-data-grid",
    title: "How to use Data Admin safely",
    category: "admin",
    level: "advanced",
    summary: "Edit, add and delete rows in the back-office reference tables, and understand what is protected.",
    tags: ["data admin", "admin", "grid", "edit rows", "reference tables", "spine", "read-only", "delete row", "add row", "cents", "back office", "permissions"],
    body: [
      { type: "p", text: "**Data Admin** is a spreadsheet-style grid for correcting reference data directly in the database. It is powerful, so it is limited by permission and by table." },
      { type: "callout", tone: "warn", text: "Changes save immediately and flow into dashboards and reports. There is no undo button. For everyday changes, use the normal screens instead." },
      { type: "h", text: "Who can use it" },
      { type: "p", text: "You need the **Data Admin grid** permission. Reading, adding, editing and deleting are separate permissions, so someone might be able to look but not change anything. By default only owners and admins have it. Others will not see the Data Admin card at all." },
      { type: "h", text: "Edit a row" },
      {
        type: "steps",
        items: [
          { title: "Open Data Admin", to: "/admin", toLabel: "Open Data Admin" },
          { title: "Click a table button", body: "It opens on Ad spend. A padlock on the button means read-only." },
          { title: "Change a value", body: "Type in a box or use the dropdown. Nothing is saved yet, and the Save icon becomes active." },
          { title: "Click the Save icon at the end of the row", body: "You see Saved and the grid reloads. If a value is not allowed, an error message tells you why." },
        ],
      },
      { type: "h", text: "Add a row" },
      { type: "steps", items: [{ title: "Use the bar under the table", body: "Fill in the boxes. Columns the system controls are not shown." }, { title: "Click Add row", body: "You see Row added. Empty required numbers are rejected." }] },
      { type: "h", text: "Delete a row" },
      { type: "steps", items: [{ title: "Click the trash icon", body: "A box asks Delete this row?" }, { title: "Click Delete to confirm", body: "The row is permanently removed." }] },
      { type: "h", text: "Rules of the grid" },
      {
        type: "list",
        items: [
          "The grid shows the **newest 500 rows** of the chosen table.",
          "**Core tables** (Accounts and MRR snapshots) show a padlock and are read-only. They drive your revenue and pipeline numbers, so change them through the app.",
          "Columns with a padlock in the header, such as id, origin and created date, are set by the system and cannot be edited.",
          "**Money columns are in cents.** Type 150000 for $1,500.",
          "**Dates** are written year-month-day, such as 2026-03-15.",
          "Some columns are dropdowns (for example Channel), and true and false columns are dropdowns too.",
          "New rows are tagged with your agency and the current data mode (demo or live) automatically.",
        ],
      },
      { type: "callout", tone: "tip", text: "Hover the (i) icon on a column heading, or hover a table button, for a plain-language description." },
      {
        type: "faq",
        items: [
          { q: "Why is the Save icon greyed out?", a: "It only activates after you change a value in that row." },
          { q: "Why do I get an error when saving?", a: "A value was not valid, for example text in a number box or a dropdown value that is not allowed. Fix it and save again." },
          { q: "Why can't I edit Accounts?", a: "Accounts is a core table. Edit accounts from Pipeline or the account page so the metrics stay consistent." },
        ],
      },
    ],
    related: ["admin-data-tables", "learning-home-tour", "glossary"],
  },

  {
    id: "admin-data-tables",
    title: "Data Admin tables: what each one holds",
    category: "admin",
    level: "advanced",
    summary: "A reference for the five tables in Data Admin: Accounts, Ad spend, Setter activity, Upcoming items and MRR snapshots.",
    tags: ["data admin", "tables", "accounts", "ad spend", "setter activity", "upcoming items", "mrr snapshots", "columns", "cents", "reference"],
    body: [
      { type: "p", text: "Data Admin lists five tables. Two are read-only core tables, and three can be edited." },
      {
        type: "table",
        head: ["Table", "Editable?", "What it holds", "What editing changes"],
        rows: [
          ["Accounts", "No (core)", "Every prospect and customer, with contact details, status, revenue and ticket size.", "Nothing here. Edit accounts from Pipeline or the account page."],
          ["Ad spend", "Yes", "Advertising spend and leads by date and channel (meta, google or other).", "The ad spend and cost-per-lead numbers on your dashboards."],
          ["Setter activity", "Yes", "Daily setter counts: leads received, dials, conversations, sets and speed to lead.", "Mainly demo (seed) data. The setter metrics read this table only for demo data, so edits matter little in live mode."],
          ["Upcoming items", "Yes", "The to-do items listed on the daily status report, with a done flag.", "What appears in the upcoming list."],
          ["MRR snapshots", "No (core)", "Daily snapshots of monthly recurring revenue, active and trial accounts, and goal.", "Nothing here. They are protected so revenue history stays consistent."],
        ],
      },
      { type: "h", text: "Column guide" },
      {
        type: "table",
        head: ["Column", "Meaning"],
        rows: [
          ["d", "The date, as year-month-day."],
          ["channel", "Where ad money went: meta, google or other."],
          ["spend_cents", "Amount spent, in cents. 150000 means $1,500."],
          ["leads", "Leads that spend produced."],
          ["leads_received, dials, conversations, sets", "Whole-number daily counts for a setter."],
          ["avg_speed_to_lead_sec", "Average seconds taken to first contact a new lead."],
          ["text, created_d, done", "The to-do wording, the date it was added, and whether it is finished."],
          ["notes", "Free text for your own reference."],
          ["id, source (origin), created_at", "System columns with a padlock. They cannot be edited."],
        ],
      },
    ],
    related: ["admin-data-grid", "learning-home-tour", "glossary"],
  },
];
